// Unverändert übernommen aus SRS-Probe/src/lib/werkbeteiligung.js (Stand SRS-Probe 44).
// Gleiche GEMA-Regeln wie in der Werkanmeldung. Bei Änderungen in SRS hier mitziehen.
// ═════════════════════════════════════════════════════════════════════════════
//  Reale Werkbeteiligung aus den GEMA-Splits
//
//  Im GEMA-Portal meldet man je Urheber seine Werkbeteiligung an (z. B. 20 %).
//  Die GEMA teilt sie nach Rechteart auf (Verteilungsplan §§ 191–196):
//   · Der Verleger ist eine Rolle im Anteil seines Urhebers: Er erhält im
//     Aufführungsrecht (AR) 33,33 %, im Vervielfältigungsrecht (VR) 40 % davon (§ 194).
//   · Musik/Text: im AR frei vereinbar (Musik mind. 35,2 %, Text mind. 19,8 %, § 191),
//     sonst und im VR je 50 % (§ 192). Typisch: AR 64/36, VR 50/50.
//   · Mehrere Urheber derselben Berufsgruppe teilen nach der Werkanmeldung (§ 193).
//     Dieser Anteil an der Musik bzw. am Text bleibt in AR und VR exakt gleich
//     (§ 194 Abs. 1) – das ist die Prüfung, ob richtig angemeldet wurde.
//  Die Werkbeteiligung je Person ist der Wert aus der Werkanmeldung (= AR-Sicht
//  inkl. Verlag). Aus dem VR ergibt sich derselbe Wert, wenn Musik/Text 50/50
//  vereinbart ist; bei abweichender Vereinbarung nur der Anteil je Berufsgruppe.
//  Bearbeiter (§ 195) und Spezialtextdichter (§ 196) werden mitgerechnet; der
//  Bearbeiter erhält im VR statt eines Anteils einen Zuschlag (§ 6 Abs. 4).
//
//  Der Verlagsanteil wird über „Vertreten durch“ den Urhebern zugeordnet, und
//  zwar im Verhältnis ihrer eigenen Anteile.
//  Reine Funktion ohne Abhängigkeiten (läuft im Browser, auf dem Server und in Tests).
// ═════════════════════════════════════════════════════════════════════════════

const nameKey = (n) => String(n || '').replace(/^["']+|["']+$/g, '').toUpperCase()
  .replace(/[.,\-–]/g, ' ').replace(/&/g, ' UND ').replace(/\s+/g, ' ').trim();
const zahl = (v) => parseFloat(String(v ?? '').replace(',', '.')) || 0;
const r2 = (x) => Math.round(x * 100) / 100;
export const istVerlagsRolle = (rolle) => /verlag|verleger|publisher/i.test(String(rolle || ''));
const OHNE_VERLAG = new Set(['COPYRIGHT CONTROL', 'ANDERER VERLAG', 'KEIN VERLAG', '']);
const TOLERANZ = 0.1;   // Prozentpunkte (33,33 geht nicht genau auf)
// Rolle → Werkteil: Text (Textdichter, Spezialtextdichter, Übersetzer), Musik (Komponist,
// Bearbeiter) oder beides, wenn die GEMA beide Rollen in einer Zeile meldet
const istBearbeiter = (rolle) => /bearbeit|arrang/i.test(String(rolle || ''));
export const werkteilVonRolle = (rolle) => {
  const r = String(rolle || ''), text = /text|lyri|übersetz|ubersetz|autor|author/i.test(r), musik = /kompon|composer|bearbeit|arrang/i.test(r);
  return text && musik ? 'beide' : text ? 'text' : 'musik';
};

// beteiligte: [{ name, rolle, vertretenDurch, ar, vr, vertritt?, vertrittRolle? }]  (ein Eintrag je GEMA-Zeile)
// Verlagszeilen aus dem GEMA-Export nennen in „Vertritt“ Urheber und Rolle: dann wird der
// Verlagsanteil genau dieser Rolle zugeordnet, sonst anteilig über „Vertreten durch“.
// eigene: Namen der eigenen Verlage (zum Markieren in der Parteienliste)
export function werkbeteiligung(beteiligte = [], { eigene = [] } = {}) {
  const eigenSet = new Set(eigene.map(nameKey).filter(Boolean));
  const zeilen = beteiligte.map(b => ({ ...b, ar: zahl(b.ar), vr: zahl(b.vr) }));
  const summeAr = r2(zeilen.reduce((s, b) => s + b.ar, 0));
  const summeVr = r2(zeilen.reduce((s, b) => s + b.vr, 0));
  const mitVr = zeilen.some(b => b.vr > 0);

  // Urheber zusammenfassen (mehrere Rollen derselben Person addieren)
  const urheber = new Map();
  const verlage = new Map();
  const schreibweise = new Map();   // lesbare Schreibweise aus „Vertreten durch“
  const direkt = [], ohneSub = [];   // Verlagszeilen mit „Vertritt“ (Urheber + Rolle bzw. Verlag)
  for (const b of zeilen) {
    const k = nameKey(b.name); if (!k) continue;
    if (istVerlagsRolle(b.rolle)) {
      if (b.vertritt && b.vertritt !== '-') { direkt.push({ key: k, name: b.name, an: nameKey(b.vertritt), rolle: b.vertrittRolle || '', ar: b.ar, vr: b.vr }); if (!schreibweise.has(k)) schreibweise.set(k, b.name); continue; }
      const v = verlage.get(k) || verlage.set(k, { key: k, name: b.name, ar: 0, vr: 0 }).get(k);
      v.ar += b.ar; v.vr += b.vr;
    } else {
      const u = urheber.get(k) || urheber.set(k, { key: k, name: b.name, rollen: [], ar: 0, vr: 0, vertreten: [], jeRolle: {} }).get(k);
      u.ar += b.ar; u.vr += b.vr;
      if (b.rolle && !u.rollen.includes(b.rolle)) u.rollen.push(b.rolle);
      const rk = b.rolle || 'Urheber', rr = u.jeRolle[rk] || (u.jeRolle[rk] = { rolle: rk, ar: 0, vr: 0 });
      rr.ar += b.ar; rr.vr += b.vr;
      String(b.vertretenDurch || '').split('/').forEach(roh => { const v = nameKey(roh); if (!v) return; if (!u.vertreten.includes(v)) u.vertreten.push(v); if (!schreibweise.has(v)) schreibweise.set(v, roh.trim()); });
    }
  }
  const U = [...urheber.values()].map(u => ({ ...u, verlagAr: {}, verlagVr: {} }));
  // Direkt zugeordnete Verlagsanteile (je Urheber und Rolle)
  const anUrheber = (u, key, rolle, ar, vr) => {
    u.verlagAr[key] = (u.verlagAr[key] || 0) + ar; u.verlagVr[key] = (u.verlagVr[key] || 0) + vr;
    const rk = Object.keys(u.jeRolle).find(r => nameKey(r) === nameKey(rolle)) || null;
    (u.direkt || (u.direkt = [])).push({ rolle: rk, ar, vr });
  };
  const sub = [];   // Subverlag vertritt einen Verlag (nicht den Urheber)
  for (const d of direkt) {
    const u = U.find(x => x.key === d.an);
    if (u) { anUrheber(u, d.key, d.rolle, d.ar, d.vr); continue; }
    if (direkt.some(x => x.key === d.an)) { sub.push(d); continue; }
    const v = verlage.get(d.key) || verlage.set(d.key, { key: d.key, name: d.name, ar: 0, vr: 0 }).get(d.key); v.ar += d.ar; v.vr += d.vr;
  }
  // Subverlagsanteil auf die Urheber-Rollen des vertretenen Verlags verteilen (nach deren eigenem Anteil)
  for (const s0 of sub) {
    const ziele = direkt.filter(x => x.key === s0.an).map(x => ({ u: U.find(y => y.key === x.an), rolle: x.rolle })).filter(z => z.u);
    if (!ziele.length) { ohneSub.push(s0); continue; }
    const eigen = (z, f) => { const r = Object.values(z.u.jeRolle).find(r => nameKey(r.rolle) === nameKey(z.rolle)); return r ? r[f] : 0; };
    const sAr = ziele.reduce((t, z) => t + eigen(z, 'ar'), 0), sVr = ziele.reduce((t, z) => t + eigen(z, 'vr'), 0);
    for (const z of ziele) anUrheber(z.u, s0.key, z.rolle, sAr ? s0.ar * eigen(z, 'ar') / sAr : s0.ar / ziele.length, sVr ? s0.vr * eigen(z, 'vr') / sVr : s0.vr / ziele.length);
  }

  // Verlagsanteile auf die vertretenen Urheber verteilen (im Verhältnis ihrer Anteile)
  const ohneZuordnung = ohneSub.map(d => ({ name: d.name, ar: r2(d.ar), vr: r2(d.vr) }));
  for (const v of verlage.values()) {
    const w = U.filter(u => u.vertreten.includes(v.key));
    const basisAr = w.reduce((s, u) => s + u.ar, 0), basisVr = w.reduce((s, u) => s + u.vr, 0);
    if (!w.length) { ohneZuordnung.push({ name: v.name, ar: r2(v.ar), vr: r2(v.vr) }); continue; }
    for (const u of w) {
      u.verlagAr[v.key] = (u.verlagAr[v.key] || 0) + (basisAr ? v.ar * u.ar / basisAr : v.ar / w.length);
      u.verlagVr[v.key] = (u.verlagVr[v.key] || 0) + (basisVr ? v.vr * u.vr / basisVr : v.vr / w.length);
    }
  }

  const warnungen = [];
  const liste = U.map(u => {
    const vAr = Object.values(u.verlagAr).reduce((s, x) => s + x, 0);
    const vVr = Object.values(u.verlagVr).reduce((s, x) => s + x, 0);
    const wbAr = r2(u.ar + vAr), wbVr = r2(u.vr + vVr);
    const verlegt = u.vertreten.some(v => !OHNE_VERLAG.has(v));
    const zugeordnet = vAr > 0 || vVr > 0;
    // Urheber ohne Zuordnung, obwohl ein Verlag am Werk steht: Schätzung nach Regelquote
    const schaetzung = !zugeordnet && !u.vertreten.length && ohneZuordnung.length ? r2(u.ar * 3 / 2) : null;
    // Je Rolle: eigener Anteil + Verlagsanteil (Verleger als Rolle im Anteil der Person)
    const jeRolle = Object.values(u.jeRolle).map(x => {
      const dir = u.direkt || [];
      const alleDirekt = dir.length && dir.every(d => d.rolle) && vAr - dir.reduce((s0, d) => s0 + d.ar, 0) < 0.01;
      const vlAr = alleDirekt ? dir.filter(d => d.rolle === x.rolle).reduce((s0, d) => s0 + d.ar, 0) : u.ar ? vAr * x.ar / u.ar : 0;
      const vlVr = alleDirekt ? dir.filter(d => d.rolle === x.rolle).reduce((s0, d) => s0 + d.vr, 0) : u.vr ? vVr * x.vr / u.vr : 0;
      return { rolle: x.rolle, teil: werkteilVonRolle(x.rolle), bearbeiter: istBearbeiter(x.rolle),
        ar: r2(x.ar), vr: r2(x.vr), verlagAr: r2(vlAr), verlagVr: r2(vlVr),
        wb: r2(x.ar + vlAr), wbVr: mitVr ? r2(x.vr + vlVr) : null };
    });
    return { name: u.name, rollen: u.rollen, jeRolle, ar: r2(u.ar), vr: r2(u.vr), wb: wbAr, wbAr, wbVr: mitVr ? wbVr : null,
      verlage: Object.keys(u.verlagAr), verlegt, zugeordnet, schaetzung, abweichung: false,
      // Verleger als Rolle im Anteil der Person: je Verlag AR- und VR-Anteil
      verlagJe: Object.keys(u.verlagAr).map(k => ({ key: k, name: schreibweise.get(k) || verlage.get(k)?.name || k, ar: r2(u.verlagAr[k]), vr: r2(u.verlagVr[k] || 0) })),
      anteilVerlag: r2(vAr), anteilVerlagVr: r2(vVr) };
  }).sort((a, b) => b.wb - a.wb || a.name.localeCompare(b.name, 'de'));

  // Werkteile je Rechteart (Verlag herausgerechnet = Summe der Werkbeteiligungen je Teil)
  const teilSumme = (feld) => { const t = { musik: 0, text: 0, beide: 0 }; liste.forEach(u => u.jeRolle.forEach(x => { t[x.teil] += x[feld] || 0; })); return t; };
  const tAr = teilSumme('wb'), tVr = mitVr ? teilSumme('wbVr') : null;
  // Anteil innerhalb der Berufsgruppe (§ 193): Komponisten unter sich, Textdichter unter sich.
  // Bearbeiter (§ 195) und Spezialtextdichter (§ 196) haben eigene Quoten und bleiben außen vor.
  const gruppe = (x) => x.teil === 'beide' || x.bearbeiter || /spezial/i.test(x.rolle) ? null : x.teil;
  const gSumme = (feld) => { const g = { musik: 0, text: 0 }; liste.forEach(u => u.jeRolle.forEach(x => { const k = gruppe(x); if (k) g[k] += x[feld] || 0; })); return g; };
  const gAr = gSumme('wb'), gVr = mitVr ? gSumme('wbVr') : null;
  // Prüfung nur, wenn das VR vollständig vorliegt (sonst fehlende Zeilen statt Fehlanmeldung).
  // Hat ein Urheber (außer Bearbeiter, § 6 Abs. 4) kein VR, liegt es woanders (Verlag, andere Gesellschaft):
  // dann ist der Vergleich nicht aussagekräftig – Hinweis statt Fehler.
  const ohneVr = mitVr ? liste.filter(u => u.jeRolle.some(x => !x.bearbeiter && x.ar > 0 && x.vr === 0)).map(u => u.name) : [];
  if (ohneVr.length) warnungen.push({ art: 'vr-woanders', namen: ohneVr });
  const vrPruefbar = mitVr && Math.abs(summeVr - 100) <= 0.5 && !ohneVr.length;
  for (const u of liste) {
    for (const x of u.jeRolle) {
      const k = gruppe(x);
      x.anteilGruppeAr = k && gAr[k] ? r2(x.wb / gAr[k] * 100) : null;
      x.anteilGruppeVr = k && gVr && gVr[k] ? r2(x.wbVr / gVr[k] * 100) : null;
      x.passt = x.anteilGruppeAr == null || x.anteilGruppeVr == null || !vrPruefbar || Math.abs(x.anteilGruppeAr - x.anteilGruppeVr) <= 0.5;
      if (!x.passt && (u.zugeordnet || !ohneZuordnung.length)) {
        u.abweichung = true;
        warnungen.push({ art: 'ar-vr', name: u.name, rolle: x.rolle, ar: x.anteilGruppeAr, vr: x.anteilGruppeVr });
      }
    }
  }
  if (ohneZuordnung.length) warnungen.push({ art: 'ohne-zuordnung', verlage: ohneZuordnung });
  if (Math.abs(summeAr - 100) > 0.01) warnungen.push({ art: 'summe-ar', summe: summeAr });
  if (mitVr && Math.abs(summeVr - 100) > 0.01) warnungen.push({ art: 'summe-vr', summe: summeVr });

  // Parteien: Verlag = Summe seiner Urheber; Urheber ohne Verlag stehen für sich
  const parteien = new Map();
  const anParteiGeben = (key, name, art, wert, wer) => {
    const p = parteien.get(key) || parteien.set(key, { name, art, wb: 0, urheber: [], eigen: eigenSet.has(key) }).get(key);
    p.wb += wert; if (wer && !p.urheber.includes(wer)) p.urheber.push(wer);
  };
  for (const u of liste) {
    const anteile = U.find(x => nameKey(x.name) === nameKey(u.name)).verlagAr;
    const summe = Object.values(anteile).reduce((s, x) => s + x, 0);
    // Urheber ohne Verlag (Copyright Control) steht für sich; fehlt nur die Zuordnung: „offen“
    if (!summe) { anParteiGeben('U:' + nameKey(u.name), u.name, u.schaetzung != null ? 'offen' : 'urheber', u.wb); continue; }
    for (const [k, x] of Object.entries(anteile)) anParteiGeben(k, schreibweise.get(k) || verlage.get(k)?.name || k, 'verlag', u.wb * x / summe, u.name);
  }
  for (const v of ohneZuordnung) anParteiGeben('V:' + nameKey(v.name), v.name, 'ohne', v.ar);
  const parteiListe = [...parteien.values()].map(p => ({ ...p, wb: r2(p.wb) }))
    .sort((a, b) => (b.eigen - a.eigen) || b.wb - a.wb);

  // Werkteile: Aufteilung Musik / Text am Werk (AR = vereinbart nach § 191, VR = 50/50 nach § 192)
  const runden = (t) => t && Object.fromEntries(Object.entries(t).map(([k, v]) => [k, r2(v)]));
  const werkteile = runden(tAr), werkteileVr = runden(tVr);

  return { urheber: liste, parteien: parteiListe, ohneZuordnung, summeAr, summeVr, mitVr, werkteile, werkteileVr,
    eigenerAnteil: r2(parteiListe.filter(p => p.eigen).reduce((s, p) => s + p.wb, 0)), warnungen };
}

// Kurzform für Listen: Anteil der eigenen Verlage am Werk
export const eigenerWerkanteil = (beteiligte, eigene) => werkbeteiligung(beteiligte, { eigene }).eigenerAnteil;

// ═════════════════════════════════════════════════════════════════════════════
//  GEMA-Regel: Splits erzeugen und nachrechnen (Verteilungsplan §§ 191–195)
//
//  Angemeldet wird die Aufteilung Komposition/Text: 64/36 oder 50/50 (ohne Text 100/0).
//   AR: Urheber 2/3, Verlag 1/3 vom Anteil.  64/36 → Komponist 42,67 · Text 24,00 · Verlag 33,33
//   VR: Musik/Text immer 50/50, Urheber 60 %, Verlag 40 %  → Komponist 30 · Text 30 · Verlag 40
//   Mehrere Urheber einer Rolle teilen nach ihrem Anteil an der Musik bzw. am Text (§ 193).
//   Bearbeiter: 1/12 (8,33 %) oder 2/12 (16,67 %) im AR, getragen vom Komponisten (2/3) und
//   seinem Verlag (1/3); im VR kein Anteil, sondern ein Zuschlag (§ 195, § 6 Abs. 4).
// ═════════════════════════════════════════════════════════════════════════════
const AUFTEILUNG = { '64/36': 0.64, '50/50': 0.5 };
// Größter-Rest-Verfahren: Werte (in %) auf ganze Cent so runden, dass die Summe genau „cent“ ergibt
function verteile(werte, cent) {
  const roh = werte.map(w => w * 100), basis = roh.map(Math.floor);
  let rest = cent - basis.reduce((s, x) => s + x, 0);
  const ord = roh.map((x, i) => [x - basis[i], i]).sort((a, b) => b[0] - a[0] || a[1] - b[1]);
  for (let k = 0; rest > 0 && ord.length; k = (k + 1) % ord.length, rest--) basis[ord[k][1]]++;
  for (let k = ord.length - 1; rest < 0 && ord.length; k = (k - 1 + ord.length) % ord.length, rest++) basis[ord[k][1]]--;
  return basis;
}
export const BEARBEITER_QUOTEN = [8.33, 16.67];

// spec: [{ name, rolle: 'K'|'T'|'B', anteil (% der Musik bzw. des Textes; Bearbeiter: 8,33 oder 16,67), verlegt,
//          verlage?: [{ name, anteil }] (Co-Verlage mit ihrem Anteil am Verlagsanteil in %) }]
// liefert je Urheberzeile { i, name, rolle, ar, vr } und je verlegter Zeile { i, rolle: 'Originalverlag', ar, vr }
export function gemaSplits(spec = [], { aufteilung = '64/36' } = {}) {
  const mitText = spec.some(u => u.rolle === 'T' && zahl(u.anteil) > 0);
  // 64/36, 50/50 oder frei vereinbart (z. B. „70/30“, § 191: Musik mind. 35,2 %, Text mind. 19,8 %)
  const frei = parseFloat(String(aufteilung).replace(',', '.')) / 100;
  const M = mitText ? (AUFTEILUNG[aufteilung] ?? (frei > 0 && frei < 1 ? frei : 0.64)) : 1, MV = mitText ? 0.5 : 1;
  const bSumme = spec.filter(u => u.rolle === 'B').reduce((s, u) => s + zahl(u.anteil), 0);
  const z = [];
  spec.forEach((u, i) => {
    const s = zahl(u.anteil) / 100;
    let gAr, gVr;
    if (u.rolle === 'B') { gAr = zahl(u.anteil); gVr = 0; }
    else if (u.rolle === 'T') { gAr = 100 * (1 - M) * s; gVr = 100 * (1 - MV) * s; }
    else { gAr = (100 * M - bSumme) * s; gVr = 100 * MV * s; }
    const rolle = { K: 'Komponist', T: 'Textdichter', B: 'Bearbeiter' }[u.rolle] || u.rolle;
    z.push({ i, name: u.name, rolle, ar: u.verlegt ? gAr * 2 / 3 : gAr, vr: u.verlegt ? gVr * 0.6 : gVr });
    // Verlagsanteil; bei Co-Verlagen nach ihrem Anteil am Verlagsanteil geteilt (§ 194 Abs. 4)
    if (u.verlegt) {
      const V = (u.verlage && u.verlage.length ? u.verlage : [{ name: '', anteil: 100 }]);
      const qs = V.reduce((t, v) => t + zahl(v.anteil), 0) || 100;
      V.forEach((v, vi) => z.push({ i, vi, name: u.name, verlag: v.name, rolle: 'Originalverlag', ar: gAr / 3 * zahl(v.anteil) / qs, vr: gVr * 0.4 * zahl(v.anteil) / qs }));
    }
  });
  // Runden wie die GEMA: erst die Summe je Rolle auf 100,00, dann innerhalb der Rolle (größter Rest)
  for (const f of ['ar', 'vr']) {
    const gruppen = new Map(); z.forEach(b => (gruppen.get(b.rolle) || gruppen.set(b.rolle, []).get(b.rolle)).push(b));
    const G = [...gruppen.values()].map((L) => ({ L, exakt: L.reduce((s, b) => s + b[f], 0) }));
    const gesamt = G.reduce((s, g) => s + g.exakt, 0);
    const ziele = verteile(G.map(g => g.exakt), Math.abs(gesamt - 100) < 1e-6 ? 10000 : Math.round(gesamt * 100));
    G.forEach((g, k) => { const c = verteile(g.L.map(b => b[f]), ziele[k]); g.L.forEach((b, j) => { b[f] = c[j] / 100; }); });
  }
  return { zeilen: z, aufteilung: mitText ? aufteilung : 'ohne Text' };
}

// Bestehende Splits nachrechnen: welche Aufteilung angemeldet ist (64/36 oder 50/50) und ob
// jede Rolle und jeder Verlag den Sollwert trifft. Toleranz 0,05 Prozentpunkte.
export function gemaPruefung(beteiligte = [], opts = {}) {
  const r = werkbeteiligung(beteiligte, opts), TOL = 0.05;
  const R = []; r.urheber.forEach(u => u.jeRolle.forEach(x => R.push({ u, x })));
  const art = (x) => x.bearbeiter ? 'B' : /spezial/i.test(x.rolle) ? 'SP' : x.teil === 'beide' ? 'KT' : x.teil === 'text' ? 'T' : 'K';
  R.forEach(o => { o.a = art(o.x); o.verlegt = (o.u.verlagJe || []).length > 0; });
  const texted = R.some(o => ['T', 'SP', 'KT'].includes(o.a));
  const nach = (a) => R.filter(o => o.a === a), Ks = nach('K'), Ts = nach('T'), Ss = nach('SP'), KTs = nach('KT'), Bs = nach('B');
  Bs.forEach(o => { o.bStd = Math.abs(o.x.wb - 8.33) <= Math.abs(o.x.wb - 16.67) ? 8.33 : 16.67; });
  const bSumme = Bs.reduce((s, o) => s + o.bStd, 0), sum = (L, f) => L.reduce((s, o) => s + f(o), 0);
  const spAr = sum(Ss, o => o.x.wb), ownT = sum(Ts, o => o.x.ar) || 1;
  Ts.forEach(o => { o.tFull = o.x.wb + spAr * o.x.ar / ownT; });

  function rechne(M) {
    const MV = texted ? 0.5 : 1;
    let ktM = 100 * M - sum(Ks, o => o.x.wb) - bSumme, ktT = 100 * (1 - M) - sum(Ts, o => o.tFull) - (Ts.length ? 0 : spAr);
    const sKT = sum(KTs, o => o.x.wb) || 1;
    if (KTs.length && (ktM < -TOL || ktT < -TOL)) { ktM = sKT * M; ktT = sKT * (1 - M); }
    KTs.forEach(o => { o.m = Math.max(ktM, 0) * o.x.wb / sKT; o.t = Math.max(ktT, 0) * o.x.wb / sKT; });
    Ks.forEach(o => { o.m = o.x.wb; o.t = 0; }); Ts.forEach(o => { o.m = 0; o.t = o.tFull; });
    const sm = sum([...Ks, ...KTs], o => o.m) || 1, st = sum([...Ts, ...KTs], o => o.t) || 1;
    let abw = 0;
    for (const o of [...Ks, ...Ts, ...KTs]) {
      const gAr = (M - bSumme / 100) * o.m / sm + (1 - M) * o.t / st, gVr = MV * o.m / sm + (1 - MV) * o.t / st;
      const q = o.verlegt ? [2 / 3, 0.6] : [1, 1], h = o.a === 'T' && Ss.length ? 0.5 : 1;
      o.soll = { ar: 100 * gAr * q[0] * h, vr: 100 * gVr * q[1] * h, verlagAr: o.verlegt ? 100 * gAr / 3 : 0, verlagVr: o.verlegt ? 100 * gVr * 0.4 : 0 };
    }
    const spA = sum(Ts, o => o.soll.ar), spV = sum(Ts, o => o.soll.vr);
    Ss.forEach(o => { const f = spAr ? o.x.wb / spAr : 1 / Ss.length; o.soll = { ar: spA * f, vr: spV * f, verlagAr: 0, verlagVr: 0 }; });
    Bs.forEach(o => { o.soll = { ar: o.verlegt ? o.bStd * 2 / 3 : o.bStd, vr: 0, verlagAr: o.verlegt ? o.bStd / 3 : 0, verlagVr: 0 }; });
    R.forEach(o => { abw += Math.abs(o.soll.ar - o.x.ar) + Math.abs(o.soll.vr - o.x.vr) + Math.abs(o.soll.verlagAr - o.x.verlagAr) + (r.mitVr ? Math.abs(o.soll.verlagVr - o.x.verlagVr) : 0); });
    return abw;
  }
  // Aufteilung erkennen: 64/36 oder 50/50; passt beides nicht, die tatsächlich angemeldete (abweichend vereinbart, § 191)
  const M_ = { ...AUFTEILUNG, 'ohne Text': 1 };
  const kandidaten = texted ? ['64/36', '50/50'] : ['ohne Text'];
  // Passt weder 64/36 noch 50/50, wird die tatsächlich angemeldete Aufteilung gesucht (auch mit Komponist+Textdichter in einer Zeile)
  if (texted && Math.min(rechne(0.64), rechne(0.5)) > 0.1 * (R.length + 1)) {
    let best = 0.5, bestAbw = Infinity;
    for (let m = 0.2; m <= 0.9; m += 0.0025) { const d = rechne(m); if (d < bestAbw) { bestAbw = d; best = m; } }
    for (let m = best - 0.0025; m <= best + 0.0025; m += 0.0001) { const d = rechne(m); if (d < bestAbw) { bestAbw = d; best = m; } }
    if (bestAbw <= 0.1 * (R.length + 1)) {
      const k = (Math.round(best * 1000) / 10).toLocaleString('de-DE') + '/' + (Math.round((1 - best) * 1000) / 10).toLocaleString('de-DE');
      M_[k] = best; kandidaten.push(k);
    }
  }
  const aufteilung = kandidaten.reduce((best, k) => (rechne(M_[k]) < rechne(M_[best]) - 0.01 ? k : best));
  rechne(M_[aufteilung]);
  const abweichend = !['64/36', '50/50', 'ohne Text'].includes(aufteilung);
  const mA = M_[aufteilung], unterMinimum = abweichend && (mA < 0.352 - 0.0005 || 1 - mA < 0.198 - 0.0005);

  // Ergebnis je Rolle und je Verlag (Verlag: Summe über die vertretenen Rollen)
  const rollen = R.map(o => {
    const s = { ar: r2(o.soll.ar), vr: r2(o.soll.vr), verlagAr: r2(o.soll.verlagAr), verlagVr: r2(o.soll.verlagVr) };
    const okAr = Math.abs(s.ar - o.x.ar) <= TOL, okVr = !r.mitVr || Math.abs(s.vr - o.x.vr) <= TOL;
    return { name: o.u.name, rolle: o.x.rolle, art: o.a, ist: { ar: o.x.ar, vr: o.x.vr }, soll: s, okAr, okVr, ok: okAr && okVr };
  });
  const verlage = {};
  R.forEach(o => {
    const vj = o.u.verlagJe || [], tot = vj.reduce((s, v) => s + v.ar, 0);
    vj.forEach(v => {
      const f = tot ? v.ar / tot : 1 / vj.length, k = v.key, e = verlage[k] || (verlage[k] = { name: v.name, ist: { ar: 0, vr: 0 }, soll: { ar: 0, vr: 0 } });
      e.soll.ar += o.soll.verlagAr * f; e.soll.vr += o.soll.verlagVr * f;
    });
  });
  r.urheber.forEach(u => (u.verlagJe || []).forEach(v => { const e = verlage[v.key]; if (e) { e.ist.ar += v.ar; e.ist.vr += v.vr; } }));
  Object.values(verlage).forEach(e => {
    ['ar', 'vr'].forEach(f => { e.ist[f] = r2(e.ist[f]); e.soll[f] = r2(e.soll[f]); });
    e.okAr = Math.abs(e.ist.ar - e.soll.ar) <= TOL * 2; e.okVr = !r.mitVr || Math.abs(e.ist.vr - e.soll.vr) <= TOL * 2; e.ok = e.okAr && e.okVr;
  });
  // Regeltabelle wie im GEMA-Portal: Summe je Rolle, Ist und Soll
  const spalten = { K: 'Komponist', T: 'Textdichter', KT: 'Komponist und Textdichter', SP: 'Spezialtextdichter', B: 'Bearbeiter' };
  const tabelle = Object.entries(spalten).filter(([a]) => R.some(o => o.a === a)).map(([a, titel]) => {
    const L = rollen.filter(x => x.art === a);
    return { art: a, titel, ist: { ar: r2(sum(L, x => x.ist.ar)), vr: r2(sum(L, x => x.ist.vr)) }, soll: { ar: r2(sum(L, x => x.soll.ar)), vr: r2(sum(L, x => x.soll.vr)) } };
  });
  const V = Object.values(verlage);
  const vZeilen = beteiligte.filter(b => istVerlagsRolle(b.rolle));
  tabelle.push({ art: 'V', titel: 'Verlag', ist: { ar: r2(vZeilen.reduce((t, b) => t + zahl(b.ar), 0)), vr: r2(vZeilen.reduce((t, b) => t + zahl(b.vr), 0)) }, soll: { ar: r2(sum(V, e => e.soll.ar)), vr: r2(sum(V, e => e.soll.vr)) } });
  tabelle.forEach(t => { t.okAr = Math.abs(t.ist.ar - t.soll.ar) <= 0.1; t.okVr = !r.mitVr || Math.abs(t.ist.vr - t.soll.vr) <= 0.1; });
  const ok = rollen.every(x => x.ok) && V.every(e => e.ok) && !r.ohneZuordnung.length;
  return { aufteilung, abweichend, unterMinimum, rollen, verlage, tabelle, ok, mitVr: r.mitVr };
}
